import { WuiButton } from '@jwplayer/wui-react';
import type { AnchorHTMLAttributes, DetailedHTMLProps } from 'react';
import { type LinkProps, Link as ReactRouterLink, useHistory } from 'react-router-dom';

import { useCurrentSiteId } from '/module/sites/hooks';

import type { LocationDescriptor, RouteName } from './types';
import { getLocationDescriptorObject } from './util';

type ButtonProps = Omit<React.ComponentProps<typeof WuiButton>, 'as' | 'disabled'>;

export function Link<
  T extends Omit<LinkProps, 'to'> & {
    to: LocationDescriptor<R>;
    // LinkProps does not have the correct type definitions
    referrerPolicy?: DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>['referrerPolicy'];
  } & {
    disabled?: boolean;
    'data-test'?: string;
    as?: ['button', ButtonProps];
  },
  R extends RouteName = RouteName,
>(props: T) {
  const currentSiteId = useCurrentSiteId();
  const locationDescriptor = getLocationDescriptorObject(props.to, {
    routeParams: {
      siteId: currentSiteId,
    },
  });

  if (locationDescriptor.params) {
    delete locationDescriptor.params;
  }

  const disabledProp =
    props.disabled || !locationDescriptor.pathname
      ? {
          onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
            // Disable href
            e.preventDefault();
          },
          disabled: 'disabled',
          tabIndex: -1,
        }
      : {};

  const { as, disabled, ...rest } = props;
  const history = useHistory();

  if (as && as[0] === 'button') {
    const buttonProps = as[1] || {}; // ButtonProps
    return (
      <WuiButton
        {...buttonProps}
        data-test={props['data-test']}
        disabled={disabled}
        as={[
          'a',
          {
            // Wui returns an <a> disguised as button
            href: locationDescriptor.pathname ? locationDescriptor.pathname + locationDescriptor.search : undefined,
            target: props.target,
            referrerPolicy: props.referrerPolicy,
          },
        ]}
        onClick={(e) => {
          if (locationDescriptor.external && locationDescriptor.pathname) {
            return;
          }

          if (!props.target) {
            // Opening a new tab is not required
            // Manually direct to react-router path instead of refreshing with href
            e.preventDefault();
            history.push(locationDescriptor);
          }
        }}
      />
    );
  }

  if (locationDescriptor.external && locationDescriptor.pathname) {
    return <a {...rest} {...disabledProp} href={locationDescriptor.pathname} />;
  }

  return <ReactRouterLink {...rest} {...disabledProp} to={locationDescriptor} />;
}
